import { DisplayDate, DisplayTime } from '@tock/time';
import { Fragment } from 'react';
import { IconCalendar, IconPeopleTeam, IconPlate } from '@tock/icons';
import { IconCalendar as IconCalendarLegacy, IconClock } from 'ui/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Link, PlainButton, Typography } from 'omakase';
import { WaitlistPreviewCardFragment } from 'generated/graphql';
import { centerCropUrl } from '@tock/images';
import { css } from '@emotion/react';
import { gql } from '@apollo/client/core';
import { pluralizeWord } from '@tock/shared/utils/pluralize';
import LazyImage from '@tock/shared/components/LazyImage';

interface Props {
  data: WaitlistPreviewCardFragment;
  onClickDelete: (id: number | undefined) => void;
}

export const WAITLIST_PREVIEW_CARD_FRAGMENT = gql`
  fragment WaitlistPreviewCard on WaitlistEntity {
    id
    business {
      id
      domainName
      name
      profileImages {
        altText
        backingUrl
        dominantColor
      }
    }
    sortedUpcomingDates {
      date
      id
      startTimeInclusive
      endTimeInclusive
    }
    desiredSize
    hasRequestBeenFulfilled
    ticketType {
      id
      name
    }
  }
`;

const WaitlistPreviewCard = ({
  data: { business, hasRequestBeenFulfilled, ticketType, sortedUpcomingDates, desiredSize, id },
  onClickDelete,
}: Props) => {
  if (sortedUpcomingDates.length === 0) {
    return null;
  }

  const image = business?.profileImages?.[0];
  const desiredTimeRange = sortedUpcomingDates[0];

  const businessUrl = `/${business?.domainName}`;

  const singleTime =
    desiredTimeRange?.startTimeInclusive === desiredTimeRange?.endTimeInclusive
      ? desiredTimeRange?.startTimeInclusive
      : null;

  return (
    <div
      className="ReservationPreviewCard ReservationPreviewCard--feedback ReservationPreviewCard-data"
      css={css`
        cursor: default;
      `}
    >
      <div className="ReservationPreviewCard-details ReservationPreviewCard-details--feedback">
        <>
          <div className="ReservationPreviewCard-headingSection">
            {business?.name && (
              <Link href={businessUrl} size="large" className="u-truncatedText">
                {business.name}
              </Link>
            )}
            {!desiredTimeRange && (
              <div className="ReservationPreviewCard-subtitle">
                <div className="u-truncatedText">{ticketType?.name}</div>
              </div>
            )}
          </div>

          <div className="ReservationPreviewCard-detailsSection">
            <ul
              css={css`
                li {
                  display: flex;
                  align-items: center;

                  &:not(:first-of-type) {
                    margin-top: 8px;
                  }
                }
                svg {
                  align-self: flex-start;
                  margin-right: 8px;
                }
                p {
                  line-height: 1.5;
                }
              `}
            >
              {desiredTimeRange ? (
                <>
                  <li>
                    <IconPeopleTeam />
                    <Typography variant="body02" color="fgDefault" data-testid="guest-info">
                      {`${desiredSize} ${pluralizeWord(desiredSize ?? 0, 'guest')}`}
                    </Typography>
                  </li>
                  <li>
                    <IconCalendar />
                    <Typography data-testid="reservation-date">
                      {sortedUpcomingDates.map((date, i) => (
                        <Fragment key={i}>
                          <DisplayDate
                            dateValue={date.date ?? undefined}
                            displayMonthAs={'short'}
                            displayWeekdayAs={'short'}
                            showWeekday={false}
                          />
                          {i < sortedUpcomingDates.length - 1 ? <br /> : null}
                        </Fragment>
                      ))}
                    </Typography>
                  </li>
                  <li>
                    <IconPlate />
                    <Typography variant="body01">{ticketType?.name ?? 'Any experience'}</Typography>
                  </li>
                  {desiredTimeRange && (
                    <li>
                      <IconClock />
                      <Typography>
                        {singleTime ? (
                          <DisplayTime timeValue={singleTime} />
                        ) : (
                          <>
                            <DisplayTime timeValue={desiredTimeRange.startTimeInclusive ?? ''} />
                            {` - `}
                            <DisplayTime timeValue={desiredTimeRange.endTimeInclusive ?? ''} />
                          </>
                        )}
                      </Typography>
                    </li>
                  )}
                </>
              ) : (
                <>
                  <li>
                    <IconMigrationUtil
                      legacyIcon={<IconCalendarLegacy />}
                      newIcon={<IconCalendar />}
                    />
                    <Typography
                      variant="body02"
                      color="fgDefault"
                      data-testid="reservation-date"
                      css={css`
                        line-height: 1.5;
                      `}
                    >
                      {sortedUpcomingDates.map((date, i) => (
                        <Fragment key={i}>
                          <DisplayDate
                            dateValue={date.date ?? undefined}
                            displayMonthAs={'short'}
                            displayWeekdayAs={'short'}
                            showWeekday={false}
                          />
                          {i < sortedUpcomingDates.length - 1 ? <br /> : null}
                        </Fragment>
                      ))}
                    </Typography>
                  </li>
                  <li>
                    <IconPeopleTeam />
                    <Typography variant="body02" color="fgDefault" data-testid="guest-info">
                      {`${desiredSize} ${pluralizeWord(desiredSize ?? 0, 'guest')}`}
                    </Typography>
                  </li>
                </>
              )}
            </ul>
          </div>
        </>

        {!hasRequestBeenFulfilled && (
          <PlainButton
            variant="primary"
            size="small"
            onClick={() => onClickDelete(id)}
            css={css`
              :hover {
                cursor: pointer;
              }
            `}
          >
            Delete
          </PlainButton>
        )}
      </div>
      {image && image.backingUrl && (
        <div className="ReservationPreviewCard-imageWrapper">
          <LazyImage
            src={centerCropUrl(image.backingUrl, { width: 350, height: 350 })}
            alt={image.altText || undefined}
            backgroundColor={image.dominantColor || undefined}
            className="ReservationPreviewCard-image"
          />
        </div>
      )}
    </div>
  );
};

export default WaitlistPreviewCard;
